import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ZoomIn, ZoomOut } from "lucide-react";

// Pan + zoom photo adjuster for profile photos. Hand rolled (no cropper
// dependency): the image moves under a fixed circular viewport via CSS
// transform, and Save redraws exactly that framing onto a 640px canvas and
// hands back a JPEG blob. Works for fresh uploads AND the already saved
// photo (same origin /uploads, so the canvas never taints).
const VIEW = 288; // on-screen viewport square (px)
const OUT = 640; // exported square (px)
const MAX_ZOOM = 3;

export default function PhotoAdjustModal({ open, src, onCancel, onSave }) {
  const [img, setImg] = useState(null); // HTMLImageElement once loaded
  const [localSrc, setLocalSrc] = useState(null); // what we actually render
  const [loadFailed, setLoadFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const dragRef = useRef(null);
  // WHY a ref mirror of zoom: pointer handlers can fire several times
  // between renders (pinch especially), so they read the ref for the truly
  // current value instead of a possibly stale state closure.
  const zoomRef = useRef(1);
  const pointersRef = useRef(new Map()); // active pointers, for pinch
  const pinchRef = useRef(null); // {dist, zoom} captured at pinch start

  // Load the photo into a LOCAL blob first (fetch with cookies), then decode
  // from that. This works identically for fresh files (already blob:) and
  // the saved photo URL, and keeps the canvas untainted without relying on
  // CORS attributes — which is what broke re-adjusting the saved photo.
  useEffect(() => {
    if (!open || !src) return undefined;
    let cancelled = false;
    let revoke = null;
    setZoom(1);
    zoomRef.current = 1;
    pointersRef.current.clear();
    pinchRef.current = null;
    setOffset({ x: 0, y: 0 });
    setImg(null);
    setLocalSrc(null);
    setLoadFailed(false);
    (async () => {
      let url = src;
      if (!/^(blob:|data:)/.test(src)) {
        try {
          const res = await fetch(src, { credentials: "include" });
          if (!res.ok) throw new Error(String(res.status));
          const blob = await res.blob();
          url = URL.createObjectURL(blob);
          revoke = url;
        } catch {
          if (!cancelled) setLoadFailed(true);
          return;
        }
      }
      if (cancelled) return;
      const el = new Image();
      el.onload = () => {
        if (!cancelled) {
          setImg(el);
          setLocalSrc(url);
        }
      };
      el.onerror = () => {
        if (!cancelled) setLoadFailed(true);
      };
      el.src = url;
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [open, src]);

  // Escape closes (parity with the other sheets).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  // Cover-fit base scale, then the user's zoom on top.
  const baseScale = img ? Math.max(VIEW / img.naturalWidth, VIEW / img.naturalHeight) : 1;
  const scale = baseScale * zoom;

  const clampOffset = useCallback(
    (o, z) => {
      if (!img) return o;
      const s = baseScale * z;
      const maxX = Math.max(0, (img.naturalWidth * s - VIEW) / 2);
      const maxY = Math.max(0, (img.naturalHeight * s - VIEW) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, o.x)),
        y: Math.min(maxY, Math.max(-maxY, o.y)),
      };
    },
    [img, baseScale]
  );

  // Distance between the two active pointers (only called when both exist).
  function pinchDist() {
    const [a, b] = [...pointersRef.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y) || 1;
  }

  function onPointerDown(e) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      // WHY: a second finger means pinch, so the one finger drag stops —
      // otherwise the image would jump around while zooming.
      dragRef.current = null;
      pinchRef.current = { dist: pinchDist(), zoom: zoomRef.current };
    } else {
      dragRef.current = { x: e.clientX, y: e.clientY, start: offset };
    }
  }

  function onPointerMove(e) {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    // Pinch: zoom by the ratio of the current finger distance to the
    // distance when the second finger landed.
    if (pinchRef.current && pointersRef.current.size >= 2) {
      applyZoom(pinchRef.current.zoom * (pinchDist() / pinchRef.current.dist));
      return;
    }
    const d = dragRef.current;
    if (!d) return;
    setOffset(
      clampOffset(
        { x: d.start.x + (e.clientX - d.x), y: d.start.y + (e.clientY - d.y) },
        zoomRef.current
      )
    );
  }

  function onPointerUp(e) {
    if (e?.pointerId != null) pointersRef.current.delete(e.pointerId);
    // Lifting either finger ends the pinch AND the drag; a fresh gesture
    // starts cleanly on the next pointerdown.
    pinchRef.current = null;
    dragRef.current = null;
  }

  function applyZoom(z) {
    const prev = zoomRef.current;
    const next = Math.min(MAX_ZOOM, Math.max(1, z));
    if (next === prev) return;
    // WHY the offset rescale: offset is measured in on-screen pixels of the
    // SCALED image, so after a zoom change the old offset points at a
    // different part of the photo. Multiplying by newScale/oldScale keeps
    // whatever was centered in the circle centered while zooming.
    const ratio = next / prev;
    zoomRef.current = next;
    setZoom(next);
    setOffset((o) => clampOffset({ x: o.x * ratio, y: o.y * ratio }, next));
  }

  async function save() {
    if (!img || saving) return;
    setSaving(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUT;
      canvas.height = OUT;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, OUT, OUT);
      const r = OUT / VIEW;
      const w = img.naturalWidth * scale * r;
      const h = img.naturalHeight * scale * r;
      const x = (VIEW / 2 + offset.x) * r - w / 2;
      const y = (VIEW / 2 + offset.y) * r - h / 2;
      ctx.drawImage(img, x, y, w, h);
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.9)
      );
      if (blob) await onSave(blob);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[110] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          onClick={onCancel}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Adjust your photo"
            className="relative w-full rounded-t-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-ambient sm:max-w-md sm:rounded-3xl sm:pb-6"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={onCancel}
              className="absolute right-3 top-3 rounded-full p-2 text-brand-secondary transition hover:bg-neutral-100 hover:text-brand-ink"
            >
              <X size={18} />
            </button>

            <h2 className="font-display text-xl font-semibold tracking-tight text-brand-ink">
              Adjust your photo
            </h2>
            <p className="mt-1 text-sm text-brand-secondary">
              Drag to reposition and pinch or slide to zoom. What you see in the circle is what everyone sees.
            </p>

            <div className="mt-5 flex justify-center">
              <div
                className="relative touch-none select-none overflow-hidden rounded-2xl bg-neutral-900"
                style={{ width: VIEW, height: VIEW, cursor: dragRef.current ? "grabbing" : "grab" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              >
                {img && localSrc && (
                  <img
                    src={localSrc}
                    alt=""
                    draggable={false}
                    className="pointer-events-none absolute left-1/2 top-1/2 max-w-none"
                    style={{
                      width: img.naturalWidth * scale,
                      height: img.naturalHeight * scale,
                      transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                    }}
                  />
                )}
                {!img && !loadFailed && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-white/30 border-t-white" />
                  </span>
                )}
                {loadFailed && (
                  <span className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm font-medium text-white/90">
                    We could not load this photo. Close this and pick the photo again to adjust it.
                  </span>
                )}
                {/* Circle mask: everything outside the circle dims */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background: "rgba(22,24,29,0.55)",
                    WebkitMaskImage:
                      "radial-gradient(circle at center, transparent 49.5%, black 50%)",
                    maskImage:
                      "radial-gradient(circle at center, transparent 49.5%, black 50%)",
                  }}
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-full border-2 border-white/80"
                  style={{ margin: 0 }}
                />
              </div>
            </div>

            <div className="mx-auto mt-5 flex max-w-[288px] items-center gap-3">
              <ZoomOut size={16} className="shrink-0 text-brand-secondary" />
              <input
                type="range"
                min="1"
                max={MAX_ZOOM}
                step="0.01"
                value={zoom}
                aria-label="Zoom"
                onChange={(e) => applyZoom(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-brand-line accent-brand-primary"
              />
              <ZoomIn size={16} className="shrink-0 text-brand-secondary" />
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 rounded-full border border-brand-line bg-white px-4 py-3 text-sm font-semibold text-brand-ink transition hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!img || saving}
                className="flex-1 rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover disabled:opacity-60"
              >
                {saving ? "Saving" : "Save photo"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
